import * as Yup from 'yup'
import { Col, Form, Row } from 'react-bootstrap'
import LocalizedStrings from 'react-localization';
import { CustomFormControl } from '../../../../StyledComponents';
import { checkRequiredText } from '../ValidationRules';
import { ClientLanguage, getClientLanguageAsString } from "../../../../../Api"

let locStrings = new LocalizedStrings({
    de:
    {
        firstName: "Vorname*",
        firstNamePlaceholder: "Vorname eingeben",

        lastName: "Name*",
        lastNamePlaceholder: "Nachname eingeben",

        emailAddress: "E-Mail*",
        emailAddressPlaceholder: "E-Mail eingeben",
        emailAddressRequired: "Angabe ist erforderlich",
        emailAddressValidationError: "Bitte geben Sie eine gültige E-Mail-Adresse an",

        phoneNumber: "Telefon / Mobil*",
        phoneNumberPlaceholder: "Telefonnummer oder Mobiltelefonnummer eingeben",
        phoneNumberRequired: "Angabe ist erforderlich",
        phoneNumberValidationError: "Ungültige Telefonnummer",
    },
    en:
    {
        firstName: "First name*",
        firstNamePlaceholder: "Enter first name",

        lastName: "Surname*",
        lastNamePlaceholder: "Enter surname",

        emailAddress: "E-mail*",
        emailAddressPlaceholder: "Enter e-mail address",
        emailAddressRequired: "Information required",
        emailAddressValidationError: "Please enter a valid e-mail address",

        phoneNumber: "Phone / Mobile*",
        phoneNumberPlaceholder: "Enter phone number or mobile phone number",
        phoneNumberRequired: "Information required",
        phoneNumberValidationError: "Invalid phone number",
    }
});

function setLanguage(clientLanguage): void {
    switch(clientLanguage) { 
        case ClientLanguage.German: { 
           locStrings.setLanguage('de');
           break; 
        } 
        case ClientLanguage.English: { 
           locStrings.setLanguage('en'); 
           break; 
        } 
        // Otherwise "clientLanguage" is "ClientLanguage.BrowserSetting" and language used is browser setting dependent.
    } 
}

export const Schema = (settings, clientLanguage) => Yup.object().shape({
    firstName: checkRequiredText(settings, clientLanguage),
    lastName: checkRequiredText(settings, clientLanguage),
    emailAddress: Yup.string()
        .when("EMailIsValid", {
            is: () => true,
            then: (schema) => {
                setLanguage(clientLanguage);
                return schema.email(locStrings.emailAddressValidationError);
              },
        })
        .when("InformationIsRequired", {
            is: () => true,
            then: (schema) => {
                setLanguage(clientLanguage);
                return schema.required(locStrings.emailAddressRequired);
              },
        }),
    phoneNumber: Yup.string()
        .when("PhoneNumberIsValid", {
            is: () => true,
            then: (schema) => {
                setLanguage(clientLanguage);
                return schema.matches(RegExp(settings.applicationPhoneNumberRegex), locStrings.phoneNumberValidationError);
              },
        })
        .when("InformationIsRequired", {
            is: () => true,
            then: (schema) => {
                setLanguage(clientLanguage);
                return schema.required(locStrings.emailAddressRequired);
              },
        }),
});

export const NameAndContactSubForm = ({ form: { values, handleBlur, handleChange, touched, errors }, ...props }) => {
    const { colors, settings, clientLanguage } = props;

    setLanguage(clientLanguage);

    function getClientLanguage(): string { 
        return getClientLanguageAsString(clientLanguage); 
    } 
    
    return (
        <>
            <Row className="mb-3">
                <Form.Group as={Col} controlId="formFirstName">
                    <Form.Label className={"FormFirstNameText"}>{locStrings.firstName}</Form.Label>
                    <CustomFormControl
                        colors={colors}
                        placeholder={locStrings.firstNamePlaceholder}
                        type="text"
                        name="firstName"
                        value={values.firstName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isValid={touched.firstName && !errors.firstName}
                        isInvalid={touched.firstName && errors.firstName}
                        maxLength={settings.textInputLimit}
                    />
                    <Form.Control.Feedback type="invalid">
                        {errors.firstName}
                    </Form.Control.Feedback>
                </Form.Group>

                <Form.Group as={Col} controlId="formLastName">
                    <Form.Label className={"FormLastNameText"}>{locStrings.lastName}</Form.Label>
                    <CustomFormControl
                        colors={colors}
                        placeholder={locStrings.lastNamePlaceholder}
                        type="text"
                        name="lastName"
                        value={values.lastName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isValid={touched.lastName && !errors.lastName}
                        isInvalid={touched.lastName && errors.lastName}
                        maxLength={settings.textInputLimit}
                    />
                    <Form.Control.Feedback type="invalid">
                        {errors.lastName}
                    </Form.Control.Feedback>
                </Form.Group>
            </Row>

            <Form.Group className="mb-3" controlId="formEmail">
                <Form.Label className={"FormEMailText"}>{locStrings.emailAddress}</Form.Label>
                <CustomFormControl
                    colors={colors}
                    placeholder={locStrings.emailAddressPlaceholder}
                    type="email"
                    name="emailAddress"
                    value={values.emailAddress}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isValid={touched.emailAddress && !errors.emailAddress}
                    isInvalid={touched.emailAddress && errors.emailAddress}
                    maxLength={settings.emailInputLimit}
                />
                <Form.Control.Feedback type="invalid">
                    {errors.emailAddress}
                </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3" controlId="formPhoneNumber">
                <Form.Label className={"FormPhoneNumberText"}>{locStrings.phoneNumber}</Form.Label>
                <CustomFormControl
                    colors={colors}
                    placeholder={locStrings.phoneNumberPlaceholder}
                    type="text"
                    name="phoneNumber"
                    value={values.phoneNumber}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isValid={touched.phoneNumber && !errors.phoneNumber}
                    isInvalid={touched.phoneNumber && errors.phoneNumber}
                    maxLength={settings.textInputLimit}
                />
                <Form.Control.Feedback type="invalid">
                    {errors.phoneNumber}
                </Form.Control.Feedback>
            </Form.Group>
        </>
    );
};